import { useEffect, useState } from 'react'
import { useRepos } from '../../state/repos'
import type { ProjectListItem } from '../../db/repositories'
import { NewProject } from '../projects/NewProject'
import { ProjectDetail } from '../projects/ProjectDetail'

type View =
  | 'list'
  | { kind: 'new'; preselectedGarmentId?: string }
  | { kind: 'detail'; id: string }

interface Props {
  pendingNewGarmentId?: string | null
  onPendingConsumed?: () => void
}

export function ProjectsTab({ pendingNewGarmentId, onPendingConsumed }: Props) {
  const { projects } = useRepos()
  const [items, setItems] = useState<ProjectListItem[]>([])
  const [loaded, setLoaded] = useState(false)
  const [view, setView] = useState<View>('list')

  async function reload() {
    setItems(await projects.list())
    setLoaded(true)
  }

  useEffect(() => {
    void reload()
  }, [projects])

  // Apertura "Nuovo progetto" cross-tab da GarmentDetail
  useEffect(() => {
    if (pendingNewGarmentId) {
      setView({ kind: 'new', preselectedGarmentId: pendingNewGarmentId })
      onPendingConsumed?.()
    }
  }, [pendingNewGarmentId, onPendingConsumed])

  if (!loaded) return <p className="muted">Caricamento…</p>

  if (typeof view === 'object' && view.kind === 'new') {
    return (
      <NewProject
        preselectedGarmentId={view.preselectedGarmentId}
        onCreated={async (id) => {
          await reload()
          setView({ kind: 'detail', id })
        }}
        onCancel={() => setView('list')}
      />
    )
  }

  if (typeof view === 'object' && view.kind === 'detail') {
    return (
      <ProjectDetail
        projectId={view.id}
        onBack={() => setView('list')}
        onDeleted={async () => {
          await reload()
          setView('list')
        }}
      />
    )
  }

  return (
    <section className="tab-section">
      <h3>
        Progetti <span className="muted">({items.length})</span>
      </h3>

      <div className="row" style={{ marginBottom: '1rem' }}>
        <button onClick={() => setView({ kind: 'new' })}>Nuovo progetto</button>
      </div>

      {items.length === 0 ? (
        <div className="empty-state">
          <p>Nessun progetto.</p>
          <p className="hint">
            Click su "Nuovo progetto" per partire. Sceglierai un capo, le misure
            (su misura o piano taglie) e otterrai i punti calcolati.
          </p>
        </div>
      ) : (
        <div className="garments-grid">
          {items.map(item => (
            <article
              key={item.id}
              className="card garment-card clickable"
              onClick={() => setView({ kind: 'detail', id: item.id })}
              role="button"
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setView({ kind: 'detail', id: item.id })
                }
              }}
            >
              <div className="garment-card-title">{item.content.name}</div>
              <div className="garment-card-badges">
                <span className="badge category">{item.content.garmentName}</span>
                <span className="badge muted">
                  {item.type === 'made-to-measure' ? 'su misura' : 'piano taglie'}
                </span>
                {item.content.sourceSizeLabel && (
                  <span className="badge scale">tg. {item.content.sourceSizeLabel}</span>
                )}
              </div>
              <div className="garment-card-meta">
                Aggiornato il {new Date(item.updatedAt).toLocaleDateString('it-IT')}
              </div>
              <div className="garment-card-stats">
                <span>
                  <strong>{Object.keys(item.content.measurements).length}</strong> misure
                </span>
                {item.content.additionalNotes && (
                  <span className="muted">+ note</span>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  )
}
